import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Typography,
  MenuItem,
  Button,
  Menu,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Tooltip,
  IconButton,
  TextField
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ccToPcConverter } from 'helpers/commonUtils';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { useDebounce } from 'helpers';

const useStyles = makeStyles(theme => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  regexFilterPanel: {
    padding: '8px',
    width: '350px'
  },
  noOptions: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px'
  },
  filterActionPanel: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  filterDisplayItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 8px'
  },
  filterDisplayItemWithoutPadding: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  selectedLabel: {
    width: '50%'
  },
  selctedTextField: {
    width: '35%'
  },
  selectedDeleteIcon: {
    width: '10%',
    color: theme.palette.error.main
  },
  actionIcon: {
    margin: '0px 8px',
    fontWeight: 'normal',
    opacity: '1 !important',
    color: theme.palette.primary.main + '!important',
    '&.Mui-disabled': {
      color: 'rgba(0,0,0,.55) !important'
    },
  },
  selectedLabelName: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  filterDisplayItemColumn: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 8px'
  },
  filterDisplayPanel: {
    overflow: 'auto',
    maxHeight: '35vh'
  }
}));


export default function QueryFilter(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filter, action } = props;
  const { options, onChange, title, interDependency, intraDependency } = filter;

  const [isSelected, setIsSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchValue, setSearchValue] = useState({});
  const [searchApiValue, setSearchApiValue] = useState({});
  const search = useDebounce(searchApiValue, 500);
  const anchorRefE1 = React.useRef(null);
  const anchorRefE2 = React.useRef(null);

  useEffect(() => {
    setIsSelected(Object.keys(searchValue).length > 0 && options.filter(option => option.checked).length > 0);
  }, [intraDependency]);

  useEffect(() => {
    setSearchValue({});
  }, [interDependency]);

  useEffect(() => {
    let newOptions = options.map(option => {
      if (option.type === 'matrix') {
        option.rows = option.rows.map(row => {
          if (searchValue[(typeof row.name === 'string') ? row.name : row.name.text]) {
            row.regex = searchValue[(typeof row.name === 'string') ? row.name : row.name.text];
          }
          return row;
        });
      } else {
        if (searchValue[option.name]) {
          option.regex = searchValue[option.name];
        }
      }
      return option
    })
    onChange(newOptions.filter(option => option.checked));
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      dispatch(action);
    }
  }, [search]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const resetFilters = () => {
    onChange(options.map(option => { 
      return { ...option, checked: false, regex: '' }
    }));
    setSearchValue({});
    dispatch(action);
  };

  const applyFilters = () => {
    dispatch(action);
  };

  const handleSelectionChange = (event, isDelete) => {
    let newOptions = options.map(option => {
      if (option.name === event.name) {
        option.checked = !option.checked;
        if (option.type === 'matrix') {
          option.rows = [...option.rows.map(row => {
            row.regex = '';
            if (isDelete) {
              searchValue[(typeof row.name === 'string') ? row.name : row.name.text] = '';
              setSearchValue({ ...searchValue });
            }
            return { ...row, regex: '' };
          })];
        } else {
          option.regex = '';
          if (isDelete) {
            searchValue[option.name] = '';
            setSearchValue({ ...searchValue });
          }
        }
      } else {
        if (option.checked) {
          if (option.type === 'matrix') {
            option.rows = option.rows.map(row => {
              row.regex = searchValue[(typeof row.name === 'string') ? row.name : row.name.text] || '';
              return row;
            });
          } else {
            option.regex = searchValue[option.name] || '';
          }
        }
      }
      return option;
    });
    if (newOptions.filter(option => !option.checked).length === 0) {
      setSelectOpen(false);
    }
    onChange(newOptions.filter(option => option.checked));
  };

  const handleDeleteSelection = (event) => {
    handleSelectionChange(event, true);
    dispatch(action);
  }

  const handleRegexChange = (event) => {
    setSearchValue({ ...searchValue, [event.target.id]: event.target.value });
    setSearchApiValue(searchValue);
  };
  
  return (
    <>
      <Tooltip title={options.length === 0 ? `Not applicable` : ``}>
        <div>
          <Button
            aria-label={`Filter ${title}`}
            ref={anchorRefE1}
            className={isSelected ? classes.selected : classes.button}
            onClick={handleToggle}
            disabled={options.length === 0}
            endIcon={<ExpandMoreIcon />}>
            {title}
          </Button>
        </div>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRefE1.current}
        role={'presentation'}
        transition>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}>
            <Paper>
              <ClickAwayListener
                onClickAway={() => handleClose()}
              >
                <div className={classes.regexFilterPanel}>
                  <div className={classes.filterDisplayPanel}>
                    <div className={classes.filterDisplay}>
                      {options.filter(option => option.checked).length === 0 ? (
                        <Typography variant="inherit" className={classes.noOptions}>
                          No filters selected
                        </Typography>
                      ) : (
                        <form autoComplete="off">
                          {options.filter(option => option.checked).map((option, index) => (
                            option.type !== 'matrix' ? (
                              <div key={index} className={classes.filterDisplayItem}>
                                <Typography variant="inherit" className={classes.selectedLabel}>
                                  <Tooltip title={option.name}>
                                    <div className={classes.selectedLabelName}>
                                      {ccToPcConverter(option.name)}
                                    </div>
                                  </Tooltip>
                                </Typography>
                                <TextField
                                  id={option.name}
                                  className={classes.selctedTextField}
                                  variant='standard'
                                  value={searchValue[option.name] || option.regex || ''}
                                  onChange={(event) => handleRegexChange(event, option)}
                                />
                                <Tooltip title={`Remove ${ccToPcConverter(option.name)} filter`}>
                                  <IconButton
                                    aria-label="delete"
                                    className={classes.selectedDeleteIcon}
                                    onClick={() => handleDeleteSelection(option)}>
                                    <CloseIcon />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            ) : (
                              <div key={index} className={classes.filterDisplayItemColumn}>
                                <div className={classes.filterDisplayItemWithoutPadding}>
                                  <Typography variant="inherit" className={classes.selectedLabel}>
                                    <Tooltip title={option.name}>
                                      <div className={classes.selectedLabelName}>
                                        {ccToPcConverter(option.name)}
                                      </div>
                                    </Tooltip>
                                  </Typography>
                                  <Tooltip title={`Remove ${ccToPcConverter(option.name)} filter`}>
                                    <IconButton
                                      aria-label="delete"
                                      className={classes.selectedDeleteIcon}
                                      onClick={() => handleDeleteSelection(option)}>
                                      <CloseIcon />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                                <div className={classes.filterDisplayItemColumn}>
                                  {option.rows.map((row, index) => (
                                    <div key={index} className={classes.filterDisplayItem}>
                                      <Typography variant="inherit" className={classes.selectedLabel}>
                                        <Tooltip title={option.name}>
                                          <div className={classes.selectedLabelName}>
                                            {(typeof row.name === 'string') ? ccToPcConverter(row.name) : ccToPcConverter(row.name.text)}
                                          </div>
                                        </Tooltip>
                                      </Typography>
                                      <TextField
                                        id={(typeof row.name === 'string') ? row.name : row.name.text}
                                        className={classes.selctedTextField}
                                        variant='standard'
                                        value={searchValue[(typeof row.name === 'string') ? row.name : row.name.text] || row.regex || ''}
                                        onChange={(event) => handleRegexChange(event, option)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          ))}
                        </form>
                      )}
                    </div>
                  </div>
                  <div className={classes.filterActionPanel}>
                    <Tooltip title="Remove all filters">
                      <Button
                        className={classes.actionIcon}
                        disabled={options.filter(option => option.checked).length === 0}
                        onClick={() => resetFilters()}>
                        Reset
                      </Button>
                    </Tooltip>
                    <div>
                      <Tooltip title="Add filters">
                        <Button
                          className={classes.actionIcon}
                          disabled={options.filter(option => !option.checked).length === 0}
                          onClick={() => setSelectOpen(true)}
                          endIcon={<AddBoxOutlinedIcon />}
                          ref={anchorRefE2}>
                          Add Filters
                        </Button>
                      </Tooltip>
                      {selectOpen && (
                        <Menu
                          keepMounted
                          open={selectOpen}
                          onClose={() => setSelectOpen(false)}
                          anchorEl={anchorRefE2.current}>
                          {options
                          .filter(option => !option.checked)
                          .map((option, index) => {
                            return (
                              <MenuItem value={index} onClick={() => handleSelectionChange(option, false)}>
                                {ccToPcConverter(option.name)}
                              </MenuItem>
                            );
                          })}
                        </Menu>
                      )}
                    </div>
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
